/**
 * Fund discounts: the closed-end fund discount screen (F3, docs/cef_tender_build.md) and its
 * 12-month paper record. The page shows information and a paper record; nothing on it is a
 * signal and nothing here places an order.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QueryBoundary, retryTransient } from "@/lib/queryState";
import { Badge, Button, EmptyState, Panel, SegmentedControl, StatCell, Switch, etSyd, fmt } from "@/sentinel";

const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));
/** A return or discount as a signed percent with two decimals: +1.25%, −0.40%. */
const pct = (v, missing = "n/a") => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Number(v) * 100).toFixed(2)}%` : missing);
const price = (v) => (isNum(v) ? fmt(Number(v), "price") : "n/a");
const money0 = (v) => (isNum(v) ? `$${Math.round(Number(v)).toLocaleString("en-US")}` : "n/a");
const errText = (e) => fmtErr(e.response?.data?.detail) || e.message;
/** The activist filing flag as words: "Yes (date)", "No", or "Not checked" when the filings could not be read. */
const flagText = (r) => (r.activist_13d === true ? `Yes${r.last_13d_date ? ` (${r.last_13d_date})` : ""}`
                         : r.activist_13d === false ? "No" : "Not checked");

const PASS_T = 2.24;

// ── latest screen ────────────────────────────────────────────────────────────
function ScreenTable({ rows, showAll }) {
  const shown = showAll ? rows : rows.filter((r) => r.eligible);
  if (!shown.length) {
    return <p className="sn-small sn-muted" style={{ margin: 0 }}>No funds to show.</p>;
  }
  return (
    <div className="sn-table-wrap" data-testid="cef-screen-table">
      <table className="sn-table" aria-label="Closed-end funds by discount">
        <thead><tr>
          <th className="r">Rank</th><th>Ticker</th><th>Name</th><th>Category</th><th className="r">Price</th>
          <th className="r">NAV</th><th className="r">Discount</th><th className="r">Avg $ volume, 63 sessions</th>
          <th data-testid="cef-flag-col">Activist filing</th>
          {showAll && <th>Not eligible because</th>}
        </tr></thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.ticker} aria-selected={r.top10 || undefined} data-testid={`cef-row-${r.ticker}`}>
              <td className="num">
                {r.rank ?? ""}{r.top10 && <> <Badge tone="brand">Top 10</Badge></>}
              </td>
              <td className="id">{r.ticker}</td>
              <td style={{ whiteSpace: "normal" }}>{r.name || ""}</td>
              <td className="sn-small" style={{ whiteSpace: "normal" }}>{r.category || ""}</td>
              <td className="num">{price(r.price)}</td>
              <td className="num">{price(r.nav)}</td>
              <td className="num" style={{ color: "var(--text-primary)" }}>{pct(r.discount)}</td>
              <td className="num">{money0(r.dollar_volume)}</td>
              <td className="sn-small" style={{ color: r.activist_13d ? "var(--text-primary)" : undefined }}>{flagText(r)}</td>
              {showAll && <td className="sn-small">{r.eligible ? "" : r.reason}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScreenBody({ data, show, setShow }) {
  const s = data.latest;
  const rule = data.rule || {};
  const notEligible = (s.funds_listed ?? 0) - (s.eligible ?? 0);
  const earlier = (data.screens || []).filter((x) => x.id !== s.id).slice(0, 6);
  const fc = data.flag_counts || {};
  const notChecked = s.activist?.not_checked ?? 0;
  return (
    <div className="sn-stack">
      <div className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
        <span className="sn-small">As of <span className="sn-num">{s.as_of}</span></span>
        {s.month_end ? <Badge icon="Calendar" tone="brand" data-testid="cef-month-end">Month end</Badge>
                     : <Badge icon="Calendar">Mid-month, not a rebalance date</Badge>}
        {s.built_at && <span className="sn-small sn-faint">built {etSyd(s.built_at, { seconds: false })}</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cef-screen-counts">
        <StatCell size="sm" label="Funds listed" value={String(s.funds_listed ?? 0)} sub="CEFConnect daily list" />
        <StatCell size="sm" label="Eligible" value={String(s.eligible ?? 0)} sub={`${notEligible} not eligible`} />
        <StatCell size="sm" label="Widest discounts" value={String(rule.top_n ?? 10)} sub="the paper portfolio" />
        <StatCell size="sm" label="Bars missing" value={String(s.bars_errors ?? 0)} sub="funds without volume data" />
      </div>
      <SegmentedControl label="Which funds to show" value={show} onChange={setShow} size="sm"
                        options={[{ value: "eligible", label: "Eligible funds", testId: "cef-show-eligible" },
                                  { value: "all", label: "All funds, with reasons", testId: "cef-show-all" }]} />
      <ScreenTable rows={s.rows || []} showAll={show === "all"} />
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="cef-flag-note">
        {data.flag_note || "Activist filing (13D in the prior year): information only."}
        {" "}Flagged now: {fc.flagged_eligible ?? 0} of the eligible funds, {fc.flagged_top10 ?? 0} of the ten.
        {notChecked > 0 ? ` ${notChecked} fund${notChecked === 1 ? "" : "s"} could not be checked.` : ""}
      </p>
      <p className="sn-small sn-faint" style={{ margin: 0 }}>
        Discount = price / NAV - 1, recomputed from the vendor's price and NAV. Eligible: price at least ${rule.min_price ?? 5},
        average dollar volume over the last {rule.dollar_volume_window ?? 63} sessions at least {money0(rule.min_dollar_volume ?? 250000)},
        and at least {rule.min_history_months ?? 12} months since inception. The ten widest discounts are highlighted.
      </p>
      {earlier.length > 0 && (
        <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="cef-screen-history">
          Earlier screens: {earlier.map((x) => `${x.as_of}${x.month_end ? " (month end)" : ""}, ${x.eligible} eligible`).join("; ")}.
        </p>
      )}
    </div>
  );
}

function ScreenPanel() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["eq-cef-screen"], queryFn: api.eqCefScreen, retry: retryTransient });
  const [show, setShow] = useState("eligible");
  const build = useMutation({
    mutationFn: () => api.eqCefRun({}),
    onSuccess: (d) => {
      toast.success(`Screen built for ${d.as_of}: ${d.eligible} eligible of ${d.funds_listed} listed.`);
      qc.invalidateQueries({ queryKey: ["eq-cef-screen"] });
    },
    onError: (e) => toast.error(`The screen could not be built. ${errText(e)}`),
  });
  return (
    <Panel title="Latest screen" icon="ListChecks" data-testid="cef-screen"
           actions={can("owner") && (
             <Button size="sm" variant="primary" icon="RefreshCw" loading={build.isPending}
                     onClick={() => build.mutate()} data-testid="cef-run-now">Build screen now</Button>
           )}>
      <QueryBoundary query={query} loading={4} what="the fund screen" errorTestId="cef-screen-error"
                     isEmpty={(d) => !d?.latest}
                     empty={{ icon: "ListChecks", title: "No screen yet", testId: "cef-screen-empty",
                              body: "The screen is built on the last trading session of each month. An owner can build one now." }}>
        {(data) => <ScreenBody data={data} show={show} setShow={setShow} />}
      </QueryBoundary>
    </Panel>
  );
}

// ── paper record ─────────────────────────────────────────────────────────────
function monthStatus(m) {
  if (m.status === "closed") return "closed";
  if (m.status === "void") return "void";
  if (m.entry_pending) return "waiting for entry closes";
  if (m.exit_pending) return "waiting for exit closes";
  return "open";
}

function MonthsTable({ months }) {
  return (
    <div className="sn-table-wrap" data-testid="cef-paper-months">
      <table className="sn-table" aria-label="Paper months">
        <thead><tr>
          <th>Entry</th><th>Exit</th><th className="r">Funds</th><th className="r">Flagged in ten</th>
          <th className="r">Portfolio net</th><th className="r">Benchmark net</th><th className="r">Excess</th>
          <th className="r">Turnover</th><th>Status</th>
        </tr></thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.id} data-testid={`cef-month-${m.id}`}>
              <td className="num">{m.entry_date}</td>
              <td className="num">{m.exit_date || "open"}</td>
              <td className="num">{m.n_tickers ?? 0} of {m.n_benchmark ?? 0}</td>
              <td className="num">{isNum(m.n_flagged_portfolio) ? String(m.n_flagged_portfolio) : "pending"}</td>
              <td className="num">{pct(m.net_ret, "pending")}</td>
              <td className="num">{pct(m.bench_net_ret, "pending")}</td>
              <td className="num" style={{ color: "var(--text-primary)" }}>{pct(m.excess_net, "pending")}</td>
              <td className="num">{isNum(m.turnover) ? `${(Number(m.turnover) * 100).toFixed(0)}%` : "n/a"}</td>
              <td className="sn-small">{monthStatus(m)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaperBody({ data }) {
  const s = data.summary || {};
  const n = s.months_closed ?? 0;
  const target = s.target_months ?? 12;
  const judged = n >= target;
  const last = data.last_result;
  const range = isNum(s.min_excess) && isNum(s.max_excess) ? `range ${pct(s.min_excess)} to ${pct(s.max_excess)}` : null;
  const meanSub = n === 0 ? "no closed month yet" : judged && isNum(s.ci95) ? `${range}, plus or minus ${pct(s.ci95)} at 2 SE` : range;
  const pass = judged && isNum(s.t) && isNum(s.mean_excess) && Number(s.mean_excess) > 0 && Number(s.t) >= PASS_T;
  return (
    <div className="sn-stack">
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="cef-paper-status">
        Tracking is {data.enabled ? "on" : "paused"}.
        {data.last_run_at ? ` Last run ${etSyd(data.last_run_at, { seconds: false })}` : " It has not run yet"}
        {last?.date ? ` for ${last.date}` : ""}
        {last?.did?.length ? `: ${last.did.join(", ")}` : ""}.
        {last?.errors?.length ? ` Problems: ${last.errors.join("; ")}.` : ""}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cef-paper-summary">
        <StatCell size="sm" label="Months closed" value={`${n} of ${target}`}
                  sub={judged ? "the 12-month record is complete" : `${s.months_remaining ?? target - n} to go`} />
        <StatCell size="sm" label="Mean monthly excess" value={pct(s.mean_excess, "none yet")} sub={meanSub} />
        <StatCell size="sm" label="Portfolio net, mean" value={pct(s.mean_portfolio_net, "none yet")} sub="after 0.30% a side" />
        <StatCell size="sm" label="Benchmark net, mean" value={pct(s.mean_benchmark_net, "none yet")} sub="all eligible funds, equal weight" />
      </div>
      {judged && (
        <p className="sn-small" data-testid="cef-paper-verdict">
          <strong>{pass ? "Pass" : "No pass"}</strong> at {target} months: mean {pct(s.mean_excess)}, t = {isNum(s.t) ? Number(s.t).toFixed(2) : "n/a"}
          {" "}(a pass needs a positive mean and t of at least {PASS_T}).
        </p>
      )}
      {(data.months || []).length === 0 ? (
        <EmptyState icon="History" title="No paper months yet" data-testid="cef-paper-empty">
          The first paper month opens on the first trading session after the first month-end screen.
        </EmptyState>
      ) : <MonthsTable months={data.months} />}
      <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="cef-paper-note">
        Judged once at 12 months; nothing before that is a result. Returns use official closes plus cash distributions
        with an ex-date after entry and on or before exit, after 0.30% a side on the weight turned over. "Flagged in ten"
        counts the names in the ten that had an activist filing at formation; it is on record only and carries no weight.
      </p>
    </div>
  );
}

function PaperPanel() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["eq-cef-paper"], queryFn: api.eqCefPaper, retry: retryTransient });
  const toggle = useMutation({
    mutationFn: (enabled) => api.eqCefConfig({ enabled }),
    onSuccess: (c) => {
      toast.success(c.enabled ? "Paper tracking is on." : "Paper tracking is paused.");
      qc.invalidateQueries({ queryKey: ["eq-cef-paper"] });
    },
    onError: (e) => toast.error(`The setting was not saved. ${errText(e)}`),
  });
  const enabled = query.data?.enabled !== false;
  return (
    <Panel title="Paper record" icon="History" data-testid="cef-paper"
           actions={can("owner") && (
             <Switch label="Paper tracking" checked={enabled} disabled={toggle.isPending}
                     onChange={(on) => toggle.mutate(on)} data-testid="cef-paper-toggle" />
           )}>
      <QueryBoundary query={query} loading={4} what="the paper record" errorTestId="cef-paper-error">
        {(data) => <PaperBody data={data} />}
      </QueryBoundary>
    </Panel>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────
export default function EquitiesCef() {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-cef">
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="cef-intro">
        Closed-end funds often trade below the value of what they hold. This screen lists the US-listed funds that can be
        bought in size and ranks them by that discount; the ten widest discounts, held in equal weights and rebalanced
        monthly, are the paper portfolio. The rule passed a pre-registered test on 2012 to 2026 against holding all
        eligible funds, but it did not beat the S&amp;P 500. It is being tracked on paper for 12 months. Nothing here is a
        signal and nothing here places an order.
      </p>
      <ScreenPanel />
      <PaperPanel />
    </div>
  );
}
